<script lang="ts">
  import { Block } from '@reuters-graphics/graphics-components';
  import { onMount } from 'svelte';
  import {
    figureData,
    STROKE_COLOR_PRIMARY,
    STROKE_COLOR_SECONDARY,
    FIGURE_SCALE,
  } from './figureData.js';

  interface Props {
    subhead?: string;
    figures?: Array<{ title?: string; subtitle?: string }>;
    legendText?: {
      skateChange?: string;
      reverseDirection?: string;
    };
  }

  let { subhead, figures: figureOverrides = [], legendText }: Props = $props();

  // Get figures 0 and 1 (CIRCLE EIGHT and SERPENTINE)
  const figures = figureData.slice(0, 2).map((fig, i) => ({
    ...fig,
    title: figureOverrides[i]?.title || fig.title,
    subtitle: figureOverrides[i]?.subtitle || fig.subtitle,
  }));

  let visibleFigures = $state([]);
  let containerElement: HTMLElement;

  onMount(() => {
    // Existing observer for path animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(
              (entry.target as HTMLElement).dataset.index || '0'
            );
            if (!visibleFigures.includes(index)) {
              visibleFigures = [...visibleFigures, index];

              // Original animation timing for indices 0 and 1
              let baseDelay = 0;
              if (index === 1) baseDelay = 500;

              const svg = entry.target.querySelector('svg');
              if (svg) {
                const animateElements = svg.querySelectorAll('animate');
                animateElements.forEach((anim, animIndex) => {
                  let delay = baseDelay;

                  // Index 0: CIRCLE EIGHT - 2 animates
                  // First circle animates immediately, second after 2500ms
                  if (index === 0) {
                    delay += animIndex === 0 ? 0 : 2500;
                  }
                  // Index 1: SERPENTINE - 4 path animates
                  else if (index === 1) {
                    if (animIndex === 0)
                      delay += 1250; // Top circle
                    else if (animIndex === 1)
                      delay += 0; // Middle bottom half
                    else if (animIndex === 2)
                      delay += 3750; // Middle top half
                    else if (animIndex === 3) delay += 5000; // Bottom circle
                  }

                  setTimeout(() => {
                    anim.beginElement();
                  }, delay);
                });
              }
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    // Only observe cards within this component's container
    if (containerElement) {
      const cards = containerElement.querySelectorAll('.figure-card');
      cards.forEach((card) => observer.observe(card));
    }

    return () => observer.disconnect();
  });
</script>

<div bind:this={containerElement} class="figures-wrapper">
  {#if subhead}
    <div class="container">
      <Block>
        <div class="subhead figure-subtitle">{subhead}</div>
      </Block>
    </div>
  {/if}

  <div class="container">
    <Block>
      <div class="legend">
        <div class="legend-item">
          <svg
            width="100%"
            height="6"
            viewBox="0 0 20 6"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <line
              x1="0"
              y1="3"
              x2="10"
              y2="3"
              stroke={STROKE_COLOR_PRIMARY}
              stroke-width="2"
            />
            <line
              x1="10"
              y1="3"
              x2="20"
              y2="3"
              stroke={STROKE_COLOR_SECONDARY}
              stroke-width="2"
            />
          </svg>
          <span class="legend-text"
            >{legendText?.skateChange || 'Skate change'}</span
          >
        </div>
        <div class="legend-item">
          <svg
            width="100%"
            height="6"
            viewBox="0 0 20 6"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <line
              x1="0"
              y1="3"
              x2="20"
              y2="3"
              stroke={STROKE_COLOR_SECONDARY}
              stroke-width="2"
              stroke-dasharray="3,3"
            />
          </svg>
          <span class="legend-text"
            >{legendText?.reverseDirection || 'Reverse direction'}</span
          >
        </div>
      </div>
    </Block>
  </div>

  <div class="container">
    <div class="figures-grid">
      {#each figures as figure, index}
        <div class="figure-card" data-index={index}>
          <div class="figure-title">{figure.title}</div>
          <div class="figure-subtitle">{figure.subtitle}</div>
          <svg
            width={250 * FIGURE_SCALE}
            height={(figure.height / 2) * FIGURE_SCALE}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 280 {figure.height}"
          >
            {@html figure.svg}
          </svg>
        </div>
      {/each}
    </div>
  </div>
</div>

<style>
  .figures-wrapper {
    overflow-x: hidden;
  }

  .container {
    width: 100%;
    max-width: 1000px;
    margin: 0 auto;
    padding: 0 20px;
  }

  .figures-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    margin-top: 30px;
    justify-items: center;
  }

  @media (max-width: 768px) {
    .figures-grid {
      gap: 0;
    }

    .container {
      padding: 10px;
    }

    .figure-card {
      padding: 5px;
    }

    .figure-card svg {
      width: 100%;
      max-width: 100px;
      height: auto;
    }

    .figure-title {
      font-size: 0.875em;
      min-height: 2.5em;
    }

    .figure-subtitle {
      display: none;
    }
  }

  .figure-card {
    padding: 5px;
    border-radius: 8px;
    overflow: visible;
    width: 100%;
    max-width: 500px;
  }

  .figure-title {
    text-align: center;
    font-weight: bold;
    color: #2c3e50;
    font-size: 1.2em;
    min-height: 1.5em;
  }

  .figure-subtitle {
    text-align: center;
    margin-bottom: 15px;
    font-size: 1em;
    color: #666;
    min-height: 3em;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .subhead {
    text-align: left;
    justify-content: flex-start;
    font-size: 1em;
  }

  .legend {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 30px;
    margin: 30px 0;
    font-size: 1em;
  }

  .legend-item {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .legend-item svg {
    flex: 0 0 40px;
  }

  .legend-text {
    color: #666;
  }

  svg {
    display: block;
    margin: 0 auto;
    overflow: visible;
    width: 100%;
    height: auto;
    max-width: 100%;
  }
</style>
